import {
  Check,
  CheckCircle2,
  ChevronsUpDown,
  Copy,
  Download,
  Pencil,
  Save,
  Send,
  X,
} from 'lucide-react';
import {useEffect, useState} from 'react';
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetDescription,
} from '@/components/ui/Sheet';
import {Button} from '@/components/ui/Button';
import {Skeleton} from '@/components/ui/Skeleton';
import {StatusPill} from '@/components/app/StatusPill';
import {Badge} from '@/components/ui/Badge';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/DropdownMenu';
import {Input} from '@/components/ui/Input';
import {
  useResendTransaction,
  useTransaction,
  useUpdateCredit,
  useUpdateInvoice,
} from '@/lib/api/transactions';
import {formatCurrency, formatDate} from '@/lib/utils/format';
import {getDocTypeLabel} from '@/lib/utils/edi-codes';
import {DocumentStatus, type TransactionType} from '@/lib/api/types';
import {useCurrentProvider} from '@/lib/providers/useCurrentProvider';

interface Props {
  transactionId: string | null;
  transactionType?: TransactionType;
  onOpenChange: (open: boolean) => void;
}

const EDITABLE_STATUSES: DocumentStatus[] = [
  DocumentStatus.PENDING,
  DocumentStatus.PROCESSING,
  DocumentStatus.COMPLETED,
  DocumentStatus.FAILED,
  DocumentStatus.RETRY,
  DocumentStatus.MISSING_DATA,
  DocumentStatus.TRANSFER_FAILED,
  DocumentStatus.IMS_UPDATE_FAILED,
  DocumentStatus.SKIPPED,
  DocumentStatus.CANCELED,
];

interface DraftLine {
  line: number;
  description: string;
  qty: number;
  unit: string;
  unitPrice: number;
}

interface Draft {
  status?: DocumentStatus;
  poNumber: string;
  vendorCode: string;
  date: string;
  tradingPartner: string;
  lines: DraftLine[];
}

function draftFromTx(tx: ReturnType<typeof useTransaction>['data']): Draft {
  return {
    status: tx?.status,
    poNumber: tx?.poNumber ?? '',
    vendorCode: tx?.vendorCode ?? '',
    date: tx?.date ?? '',
    tradingPartner: tx?.tradingPartner ?? '',
    lines:
      tx?.lines?.map(l => ({
        line: l.line,
        description: l.description,
        qty: l.qty,
        unit: l.unit,
        unitPrice: l.unitPrice,
      })) ?? [],
  };
}

function linesEqual(a: DraftLine[], b: DraftLine[]): boolean {
  if (a.length !== b.length) return false;
  return a.every((x, i) => {
    const y = b[i];
    return (
      x.description === y.description &&
      x.qty === y.qty &&
      x.unit === y.unit &&
      x.unitPrice === y.unitPrice
    );
  });
}

export function TransactionDetailSheet({
  transactionId,
  transactionType,
  onOpenChange,
}: Props) {
  const provider = useCurrentProvider();
  const {data: tx, isLoading} = useTransaction(transactionId, transactionType);
  const update = useUpdateInvoice();
  const updateCredit = useUpdateCredit();
  const isSaving = update.isPending || updateCredit.isPending;
  const resend = useResendTransaction();
  const [draft, setDraft] = useState<Draft>(() => draftFromTx(undefined));
  const [savedFlash, setSavedFlash] = useState(false);
  const [resentFlash, setResentFlash] = useState(false);
  const [isEditing, setIsEditing] = useState(false);

  const handleResend = () => {
    if (!tx) return;
    const label =
      tx.type === 'credit' ? 'credit memo' : getDocTypeLabel(tx.docType);
    const ok = window.confirm(
      `Resend ${label} #${tx.number} to ${provider.label}? This will re-trigger downstream processing.`
    );
    if (!ok) return;
    resend.mutate(
      {id: tx.id, type: tx.type === 'credit' ? 'credit' : 'invoice'},
      {onSuccess: () => setResentFlash(true)}
    );
  };

  // Re-sync draft from server truth whenever a fresher version arrives
  // (tx.modified is the server timestamp; it changes on every successful PUT).
  // This means after a save, the detail view reflects what was actually
  // persisted, not what the user typed. Also exit edit mode on a successful
  // refetch so we land back in read-only view.
  useEffect(() => {
    setDraft(draftFromTx(tx));
    setIsEditing(false);
    // Re-sync only when the server returns a new version (id or modified
    // timestamp changes). Listing the full tx would clobber in-progress edits
    // any time React Query handed us a referentially-new object.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tx?.id, tx?.modified]);

  useEffect(() => {
    if (!savedFlash) return;
    const t = setTimeout(() => setSavedFlash(false), 2500);
    return () => clearTimeout(t);
  }, [savedFlash]);

  useEffect(() => {
    if (!resentFlash) return;
    const t = setTimeout(() => setResentFlash(false), 2500);
    return () => clearTimeout(t);
  }, [resentFlash]);

  const dirty =
    !!tx &&
    (draft.status !== tx.status ||
      draft.poNumber !== (tx.poNumber ?? '') ||
      draft.vendorCode !== (tx.vendorCode ?? '') ||
      draft.date !== (tx.date ?? '') ||
      draft.tradingPartner !== (tx.tradingPartner ?? '') ||
      !linesEqual(
        draft.lines,
        tx.lines?.map(l => ({
          line: l.line,
          description: l.description,
          qty: l.qty,
          unit: l.unit,
          unitPrice: l.unitPrice,
        })) ?? []
      ));

  const handleSave = () => {
    if (!tx || !draft.status) return;

    // The PUT endpoints require the full document payload (legacy contract),
    // so we overlay edited fields onto the raw blob the row came in with.
    if (tx.type === 'credit') {
      const raw = tx.rawInvoiceData as
        | {
            credit?: Record<string, string>;
            creditLines?: Array<Record<string, string | number | null>>;
          }
        | undefined;

      const mergedLines = (raw?.creditLines ?? []).map((rawLine, i) => {
        const d = draft.lines[i];
        if (!d) return rawLine;
        const lineAmount = d.qty * d.unitPrice;
        return {
          ...rawLine,
          cre_l_desc: d.description,
          cre_l_qty: String(d.qty),
          cre_l_unit: d.unit,
          cre_l_price: String(d.unitPrice),
          cre_l_lgoods2: lineAmount.toFixed(3),
        };
      });

      const mergedCreditData = {
        ...(raw ?? {}),
        credit: {
          ...(raw?.credit ?? {}),
          cre_order: draft.poNumber,
          cre_agent: draft.vendorCode,
          cre_date: draft.date,
          cre_customer: draft.tradingPartner,
        },
        creditLines: mergedLines,
      };

      updateCredit.mutate(
        {
          cre_number: tx.number,
          location: tx.location,
          groupDb: tx.groupDb,
          modified: tx.modified,
          provider: tx.tradingPartnerId || undefined,
          status: draft.status,
          creditData: mergedCreditData,
        },
        {onSuccess: () => setSavedFlash(true)}
      );
      return;
    }

    const raw = tx.rawInvoiceData as
      | {
          invoice?: Record<string, string>;
          invoiceLines?: Array<Record<string, string | number | null>>;
        }
      | undefined;

    const mergedLines = (raw?.invoiceLines ?? []).map((rawLine, i) => {
      const d = draft.lines[i];
      if (!d) return rawLine;
      const lineAmount = d.qty * d.unitPrice;
      return {
        ...rawLine,
        inv_l_desc: d.description,
        inv_l_qty: String(d.qty),
        inv_l_unit: d.unit,
        inv_l_price: String(d.unitPrice),
        inv_l_lgoods2: lineAmount.toFixed(3),
      };
    });

    const mergedInvoiceData = {
      ...(raw ?? {}),
      invoice: {
        ...(raw?.invoice ?? {}),
        inv_order: draft.poNumber,
        inv_agent: draft.vendorCode,
        inv_date: draft.date,
        inv_customer: draft.tradingPartner,
      },
      invoiceLines: mergedLines,
    };

    update.mutate(
      {
        inv_number: tx.number,
        location: tx.location,
        groupDb: tx.groupDb,
        modified: tx.modified,
        provider: tx.tradingPartnerId || undefined,
        status: draft.status,
        invoiceData: mergedInvoiceData,
      },
      {onSuccess: () => setSavedFlash(true)}
    );
  };

  const setField = <K extends keyof Draft>(key: K, value: Draft[K]) =>
    setDraft(d => ({...d, [key]: value}));

  const setLineField = <K extends keyof DraftLine>(
    index: number,
    key: K,
    value: DraftLine[K]
  ) =>
    setDraft(d => ({
      ...d,
      lines: d.lines.map((l, i) => (i === index ? {...l, [key]: value} : l)),
    }));

  return (
    <Sheet open={!!transactionId} onOpenChange={onOpenChange}>
      <SheetContent>
        {isLoading || !tx ? (
          <>
            <SheetHeader>
              <Skeleton className="h-6 w-48" />
              <Skeleton className="h-4 w-72 mt-2" />
            </SheetHeader>
            <div className="p-6 space-y-3 flex-1">
              <Skeleton className="h-24 w-full" />
              <Skeleton className="h-40 w-full" />
              <Skeleton className="h-40 w-full" />
            </div>
          </>
        ) : (
          <>
            <SheetHeader>
              <div className="flex items-start justify-between gap-3 pr-8">
                <div>
                  <SheetTitle className="flex items-center gap-2">
                    {getDocTypeLabel(tx.docType)} #{tx.number}
                    <Badge variant="muted" className="font-mono">
                      {tx.docType}
                    </Badge>
                  </SheetTitle>
                  <SheetDescription className="mt-1 flex items-center gap-3">
                    <StatusPill status={tx.status} />
                    <span className="capitalize">{tx.direction}</span>
                    <span>·</span>
                    <span>{tx.tradingPartner}</span>
                    <span>·</span>
                    <span>{formatDate(tx.date)}</span>
                  </SheetDescription>
                </div>
              </div>

              <div className="flex items-center gap-2 pt-3">
                {isEditing ? (
                  <>
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <Button
                          variant="outline"
                          size="sm"
                          className="gap-2 min-w-[160px] justify-between"
                        >
                          <span>{draft.status ?? 'Set status…'}</span>
                          <ChevronsUpDown className="h-3.5 w-3.5 text-muted-foreground" />
                        </Button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align="start" className="w-48">
                        <DropdownMenuLabel>Status</DropdownMenuLabel>
                        <DropdownMenuSeparator />
                        {EDITABLE_STATUSES.map(s => (
                          <DropdownMenuItem
                            key={s}
                            onClick={() =>
                              setField('status', s as DocumentStatus)
                            }
                            className="flex items-center justify-between"
                          >
                            {s}
                            {draft.status === s && (
                              <Check className="h-4 w-4" />
                            )}
                          </DropdownMenuItem>
                        ))}
                      </DropdownMenuContent>
                    </DropdownMenu>
                    <Button
                      size="sm"
                      onClick={handleSave}
                      disabled={!dirty || isSaving}
                    >
                      <Save className="h-3.5 w-3.5" />
                      {isSaving ? 'Saving…' : 'Save'}
                    </Button>
                    <Button
                      size="sm"
                      variant="ghost"
                      onClick={() => {
                        setDraft(draftFromTx(tx));
                        setIsEditing(false);
                      }}
                      disabled={isSaving}
                    >
                      <X className="h-3.5 w-3.5" />
                      Cancel
                    </Button>
                  </>
                ) : (
                  <>
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => setIsEditing(true)}
                    >
                      <Pencil className="h-3.5 w-3.5" />
                      Edit
                    </Button>
                    <Button size="sm" variant="outline">
                      <Download className="h-3.5 w-3.5" />
                      Download
                    </Button>
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={handleResend}
                      disabled={resend.isPending}
                    >
                      <Send className="h-3.5 w-3.5" />
                      {resend.isPending ? 'Resending…' : 'Resend'}
                    </Button>
                  </>
                )}
                {update.isError && (
                  <span className="inline-flex items-center gap-1.5 rounded-md border border-destructive/40 bg-destructive/10 px-2.5 py-1 text-sm font-medium text-destructive">
                    Update failed
                  </span>
                )}
                {savedFlash && (
                  <span className="inline-flex items-center gap-1.5 rounded-md border border-success/40 bg-success/10 px-2.5 py-1 text-sm font-medium text-success animate-in fade-in slide-in-from-left-1">
                    <CheckCircle2 className="h-4 w-4" />
                    Saved
                  </span>
                )}
                {resentFlash && (
                  <span className="inline-flex items-center gap-1.5 rounded-md border border-success/40 bg-success/10 px-2.5 py-1 text-sm font-medium text-success animate-in fade-in slide-in-from-left-1">
                    <CheckCircle2 className="h-4 w-4" />
                    Resent
                  </span>
                )}
              </div>
            </SheetHeader>

            <div className="flex-1 overflow-y-auto scrollbar-thin p-6">
              <div className="space-y-6">
                {tx.status === DocumentStatus.FAILED && tx.reasonForFail && (
                  <div className="rounded-lg border border-destructive/30 bg-destructive/10 p-4">
                    <div className="text-xs uppercase tracking-widest text-destructive mb-1">
                      Reason for failure
                    </div>
                    <div className="text-sm">{tx.reasonForFail}</div>
                  </div>
                )}

                <div className="grid grid-cols-2 gap-4">
                  <KV
                    label={
                      tx.type === 'credit' ? 'Credit number' : 'Invoice number'
                    }
                    value={tx.number}
                    copy
                  />
                  <KV
                    label="Amount"
                    value={
                      tx.amount
                        ? formatCurrency(tx.amount, tx.currency ?? 'USD')
                        : '—'
                    }
                  />
                  {isEditing ? (
                    <>
                      <Field label="Trading partner">
                        <Input
                          value={draft.tradingPartner}
                          onChange={e =>
                            setField('tradingPartner', e.target.value)
                          }
                        />
                      </Field>
                      <Field label="PO number">
                        <Input
                          value={draft.poNumber}
                          onChange={e => setField('poNumber', e.target.value)}
                        />
                      </Field>
                      <Field label="Vendor code">
                        <Input
                          value={draft.vendorCode}
                          onChange={e => setField('vendorCode', e.target.value)}
                        />
                      </Field>
                      <Field label="Invoice date">
                        <Input
                          type="date"
                          value={draft.date?.slice(0, 10) ?? ''}
                          onChange={e => setField('date', e.target.value)}
                        />
                      </Field>
                    </>
                  ) : (
                    <>
                      <KV
                        label="Trading partner"
                        value={tx.tradingPartner || '—'}
                      />
                      <KV label="PO number" value={tx.poNumber ?? '—'} copy />
                      <KV label="Vendor code" value={tx.vendorCode ?? '—'} />
                      <KV label="Invoice date" value={formatDate(tx.date)} />
                    </>
                  )}
                  <KV label="Location" value={tx.location || '—'} />
                  <KV label="Group" value={tx.groupDb || '—'} />
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <AddressBlock label="Bill from" addr={tx.billFrom} />
                  <AddressBlock label="Bill to" addr={tx.billTo} />
                  <AddressBlock label="Ship to" addr={tx.shipTo} />
                  <AddressBlock label="Remit to" addr={tx.remitTo} />
                </div>

                {draft.lines.length ? (
                  <div>
                    <h3 className="text-sm font-semibold mb-2">Line items</h3>
                    <div className="rounded-lg border overflow-hidden">
                      <table className="w-full text-xs">
                        <thead className="bg-muted/50">
                          <tr className="text-left">
                            <th className="px-3 py-2 font-medium w-12">Line</th>
                            <th className="px-3 py-2 font-medium">
                              Description
                            </th>
                            <th className="px-3 py-2 font-medium text-right w-24">
                              Qty
                            </th>
                            <th className="px-3 py-2 font-medium w-20">Unit</th>
                            <th className="px-3 py-2 font-medium text-right w-32">
                              Unit price
                            </th>
                            <th className="px-3 py-2 font-medium text-right w-28">
                              Amount
                            </th>
                          </tr>
                        </thead>
                        <tbody>
                          {draft.lines.map((line, i) => (
                            <tr key={i} className="border-t align-middle">
                              <td className="px-3 py-2 tabular-nums">
                                {line.line}
                              </td>
                              {isEditing ? (
                                <>
                                  <td className="px-2 py-1">
                                    <Input
                                      value={line.description}
                                      onChange={e =>
                                        setLineField(
                                          i,
                                          'description',
                                          e.target.value
                                        )
                                      }
                                      className="h-8"
                                    />
                                  </td>
                                  <td className="px-2 py-1">
                                    <Input
                                      type="number"
                                      step="0.001"
                                      value={line.qty}
                                      onChange={e =>
                                        setLineField(
                                          i,
                                          'qty',
                                          parseFloat(e.target.value) || 0
                                        )
                                      }
                                      className="h-8 text-right tabular-nums"
                                    />
                                  </td>
                                  <td className="px-2 py-1">
                                    <Input
                                      value={line.unit}
                                      onChange={e =>
                                        setLineField(i, 'unit', e.target.value)
                                      }
                                      className="h-8"
                                    />
                                  </td>
                                  <td className="px-2 py-1">
                                    <Input
                                      type="number"
                                      step="0.00001"
                                      value={line.unitPrice}
                                      onChange={e =>
                                        setLineField(
                                          i,
                                          'unitPrice',
                                          parseFloat(e.target.value) || 0
                                        )
                                      }
                                      className="h-8 text-right tabular-nums"
                                    />
                                  </td>
                                </>
                              ) : (
                                <>
                                  <td className="px-3 py-2">
                                    {line.description}
                                  </td>
                                  <td className="px-3 py-2 text-right tabular-nums">
                                    {line.qty}
                                  </td>
                                  <td className="px-3 py-2">{line.unit}</td>
                                  <td className="px-3 py-2 text-right tabular-nums">
                                    {line.unitPrice.toFixed(5)}
                                  </td>
                                </>
                              )}
                              <td className="px-3 py-2 text-right font-medium tabular-nums">
                                {formatCurrency(
                                  line.qty * line.unitPrice,
                                  tx.currency ?? 'USD'
                                )}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                ) : null}
              </div>
            </div>
          </>
        )}
      </SheetContent>
    </Sheet>
  );
}

function Field({label, children}: {label: string; children: React.ReactNode}) {
  return (
    <div>
      <div className="text-xs uppercase tracking-widest text-muted-foreground mb-0.5">
        {label}
      </div>
      {children}
    </div>
  );
}

function KV({
  label,
  value,
  copy,
  mono,
}: {
  label: string;
  value: string;
  copy?: boolean;
  mono?: boolean;
}) {
  return (
    <div>
      <div className="text-xs uppercase tracking-widest text-muted-foreground mb-0.5">
        {label}
      </div>
      <div
        className={`flex items-center gap-1 text-sm ${mono ? 'font-mono' : ''}`}
      >
        <span className="truncate">{value}</span>
        {copy && value !== '—' && (
          <button
            onClick={() => navigator.clipboard.writeText(value)}
            className="opacity-40 hover:opacity-100 transition-opacity"
            title="Copy"
          >
            <Copy className="h-3 w-3" />
          </button>
        )}
      </div>
    </div>
  );
}

function AddressBlock({
  label,
  addr,
}: {
  label: string;
  addr?: {
    name: string;
    address1?: string;
    address2?: string;
    city?: string;
    state?: string;
    postalCode?: string;
    country?: string;
  };
}) {
  if (!addr) return null;
  return (
    <div className="rounded-lg border p-3">
      <div className="text-xs uppercase tracking-widest text-muted-foreground mb-1">
        {label}
      </div>
      <div className="text-sm font-medium">{addr.name}</div>
      <div className="text-xs text-muted-foreground leading-snug mt-0.5">
        {[addr.address1, addr.address2].filter(Boolean).join(' · ')}
        {addr.address1 && <br />}
        {[addr.city, addr.state, addr.postalCode].filter(Boolean).join(', ')}
        {addr.country && <>, {addr.country}</>}
      </div>
    </div>
  );
}
